import type { IBusinessUser } from '@/domain/entities/user';
import type { IHomeObject } from '@/domain/shared/home-object';
import { SortDirection, SortField, type SortSpec } from '@/domain/shared/sortOptions';
import type { AreaValue } from '@/domain/shared/units';
import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export const sleep = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Calculates the expiry timestamp for a token
 * @param expiresInSeconds - Token lifetime in seconds
 * @returns Timestamp in milliseconds when the token expires
 */
export function calculateTokenExpiry(expiresInSeconds: number): number {
  return Date.now() + expiresInSeconds * 1000;
}

export function getInitials(user: IBusinessUser | null | undefined): string {
  const first = user?.firstName?.trim();
  const last = user?.lastName?.trim();

  const initials = [first?.[0], last?.[0]].filter(Boolean).join('').toUpperCase();

  return initials || '?';
}

export function getFullName(user: IBusinessUser | null | undefined): string {
  const first = user?.firstName?.trim() ?? '';
  const last = user?.lastName?.trim() ?? '';

  const fullName = `${first} ${last}`.trim();

  return fullName || 'Unknown User';
}

/**
 * Returns error border classes for inputs when there's an error
 * Creates a shiny red ring effect
 * @param hasError - Whether the form has an error state
 * @returns Tailwind classes for error state styling
 */
export function getErrorBorderClass(hasError?: boolean): string {
  return hasError ? 'ring-2 ring-red-500 ring-offset-2 border-red-500 focus-visible:ring-red-500' : '';
}

/**
 * Decodes a JWT token and returns the payload
 * @param token - JWT token string
 * @returns Decoded payload object or null if invalid
 */
export function decodeJwtPayload<T = Record<string, unknown>>(token: string): T | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, '=');
    const payload = JSON.parse(atob(padded));
    return payload as T;
  } catch {
    return null;
  }
}

/**
 * JWT payload structure from Cognito idToken
 */
export interface CognitoIdTokenPayload {
  sub: string;
  email?: string;
  'custom:role'?: string;
  'custom:userType'?: string;
  'cognito:username'?: string;
  [key: string]: unknown;
}

/**
 * Converts unix timestamp (seconds) to a JS Date
 */
export const unixSecondsToDate = (seconds: number): Date => {
  return new Date(seconds * 1000);
};

/**
 * Formats unix timestamp (seconds) to a locale date string
 */
export const formatUnixDate = (seconds: number, options?: Intl.DateTimeFormatOptions, locale?: string): string => {
  return unixSecondsToDate(seconds).toLocaleDateString(locale, options);
};

export const formatUnixDateTimeCompact = (seconds: number | undefined): string => {
  const d = new Date((seconds ?? 0) * 1000);

  const date = d.toLocaleDateString(undefined, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });

  const time = d.toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });

  return `${date} ${time}`;
};

export const normalizeArea = (area: unknown): AreaValue | undefined => {
  if (area == null) return undefined;

  // legacy number => assume sqm canonical
  if (typeof area === 'number') return { canonical: area, entered: { value: area, unit: 'sqm' } };

  // already correct
  if (typeof area === 'object') return area as AreaValue;

  return undefined;
};

export function sortItems(items: IHomeObject[], sort: SortSpec): IHomeObject[] {
  // IMPORTANT:
  // - Never mutate original array
  // - Manual / None keeps original order
  if (!items.length) return items;

  const { field, direction } = sort;

  // manual / backend order
  if (field === SortField.None) {
    return items;
  }

  const copy = items.slice();

  const dir = direction === SortDirection.Desc ? -1 : 1;

  const title = (x: IHomeObject) => (x.title ?? '').toLowerCase();
  const updatedAt = (x: IHomeObject) => x.updatedAt ?? 0;
  const createdAt = (x: IHomeObject) => x.createdAt ?? 0;

  switch (field) {
    case SortField.Title:
      return copy.sort((a, b) => dir * title(a).localeCompare(title(b)));

    case SortField.UpdatedAt:
      return copy.sort((a, b) => dir * (updatedAt(a) - updatedAt(b)));

    case SortField.CreatedAt:
      return copy.sort((a, b) => dir * (createdAt(a) - createdAt(b)));

    case SortField.Custom:
      // domain-specific hook (keep stable for now)
      return items;

    default:
      return items;
  }
}

export function unixSecondsToInputValue(sec: number, fieldType: 'date' | 'datetime'): string {
  // sec is unix seconds
  const d = new Date(sec * 1000);

  const pad = (n: number) => String(n).padStart(2, '0');
  const yyyy = d.getFullYear();
  const mm = pad(d.getMonth() + 1);
  const dd = pad(d.getDate());
  const hh = pad(d.getHours());
  const mi = pad(d.getMinutes());

  if (fieldType === 'date') return `${yyyy}-${mm}-${dd}`;
  return `${yyyy}-${mm}-${dd}T${hh}:${mi}`; // matches <input type="datetime-local">
}

export function inputValueToUnixSeconds(raw: string, fieldType: 'date' | 'datetime'): number | undefined {
  // raw is "YYYY-MM-DD" OR "YYYY-MM-DDTHH:mm"
  if (!raw.trim()) return undefined;

  if (fieldType === 'date') {
    const [y, m, d] = raw.split('-').map((x) => Number(x));
    if (![y, m, d].every((n) => Number.isFinite(n))) return undefined;

    // store as midnight local time
    const dt = new Date(y, m - 1, d, 0, 0, 0, 0);
    const sec = Math.floor(dt.getTime() / 1000);
    return Number.isFinite(sec) ? sec : undefined;
  }

  // datetime-local -> local time
  const dt = new Date(raw);
  const sec = Math.floor(dt.getTime() / 1000);
  return Number.isFinite(sec) ? sec : undefined;
}
